import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useField, useFormikContext } from 'formik'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import {
    selectBlockedCampaignsData,
    getBlockedCampaignCheck,
    selectBlockedCampaignsUuids,
    selectEditingCampaignCountry,
    selectEditingCampaignUuid,
    setBlockedCampaigns,
} from 'state/campaigns'
import { BlockedCampaignCheckResult } from 'services/api/campaigns'
import { STATE_KEYS } from 'state/requests'
import { RequirementCardContainer } from 'components/Shared/Fields/styles'
import Field from 'components/Shared/Fields'
import Divider from 'components/Shared/Divider'
import { BlockedCampaignsContainer } from './styles'
import Icon from 'components/Icon/Icon'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { fieldIdentifiers } from 'components/Shared/Comment'
import FieldTitleAndSubTitle from 'components/Shared/Fields/FieldTitleAndSubTitle'

const BlockedCampaigns: React.FC<{
    requirementUuid?: string
    name: string
    onRemove: () => void
    isReadOnly?: boolean
}> = ({ name, onRemove, isReadOnly = false, requirementUuid }) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const [field, meta] = useField<{ inputValue: string }>(name)
    const { setFieldValue, validateForm, setFieldError } = useFormikContext<any>()
    const editingCampaignUuid = useSelector(selectEditingCampaignUuid)
    const editingCampaignCountry = useSelector(selectEditingCampaignCountry)
    const blockedCampaignsUuids = useSelector(selectBlockedCampaignsUuids)
    const blockedCampaignsData = useSelector(selectBlockedCampaignsData)

    useEffect(() => {
        if (!blockedCampaignsUuids) dispatch(setBlockedCampaigns({ uuids: [] }))
        validateForm()
    }, [blockedCampaignsUuids])

    const handleRemove = (): void => {
        dispatch(setBlockedCampaigns({ uuids: null }))
        onRemove()
    }

    const getDisabled = (): boolean => {
        if (meta.error && (meta.error as any).inputValue) return true
        if (_.isEmpty(field.value.inputValue)) return true
        return false
    }

    const handleClickAdd = (): void => {
        if (field.value.inputValue === undefined) {
            setFieldError(`${field.name}.inputValue`, t('errorMessage.validation.enterValidCampaignId'))
            return
        }
        if (!blockedCampaignsUuids || getDisabled()) return
        if (field.value.inputValue === editingCampaignUuid) {
            setFieldError(`${field.name}.inputValue`, t('errorMessage.validation.enterAnotherCampaignId'))
            return
        }
        if (blockedCampaignsUuids.includes(field.value.inputValue)) {
            setFieldError(`${field.name}.inputValue`, t('errorMessage.validation.alreadyAddedCampaign'))
            return
        }
        const next = blockedCampaignsUuids.concat([field.value.inputValue])

        dispatch(
            getBlockedCampaignCheck({
                baseCampaignUuid: editingCampaignUuid ? editingCampaignUuid : '',
                blockedCampaignUuid: field.value.inputValue,
                stateKey: STATE_KEYS.BlockedCampaignCheck,
                onSuccess: (campaign: BlockedCampaignCheckResult) => {
                    if (campaign.country !== editingCampaignCountry) {
                        setFieldError(
                            `${field.name}.inputValue`,
                            t('errorMessage.businessLogic.enterSameCountryCampaignId'),
                        )
                        return
                    }
                    if (campaign.is_eligible_as_blocked_campaign == false) {
                        setFieldError(
                            `${field.name}.inputValue`,
                            t('errorMessage.businessLogic.cantAddActiveSubmissionConflictingCampaign'),
                        )
                        return
                    }
                    dispatch(setBlockedCampaigns({ uuids: next }))
                    setFieldValue(`${field.name}.inputValue`, null)
                },
                onCampaignNotFound: () => {
                    setFieldError(`${field.name}.inputValue`, t('errorMessage.validation.enterExistingCampaignId'))
                    return
                },
            }),
        )
    }

    const handleClickRemove = (index: number): void => {
        if (!blockedCampaignsUuids) return

        const next = [...blockedCampaignsUuids]
        next.splice(index, 1)
        dispatch(setBlockedCampaigns({ uuids: next }))
    }
    return (
        <BlockedCampaignsContainer>
            <RequirementCardContainer isCard>
                <CampaignEditorInputFieldContainer>
                    <Field.AddingListField
                        title={t('campaignEditor.requirement.conflictingCampaignsHeader')}
                        isRequired
                        subtitle={t('campaignEditor.requirement.conflictingCampaignsSubText')}
                        name={`${field.name}.inputValue`}
                        onClick={handleClickAdd}
                        onClose={handleRemove}
                        inputFieldTitle={t('campaignEditor.requirement.enterCampaignId')}
                        data-testid="blocked-campaigns-input"
                        inputFieldIdentifier={`${requirementUuid}_${fieldIdentifiers.conflictInput}`}
                    />
                </CampaignEditorInputFieldContainer>
                <Divider className="divider" />
                <div className="campaign-list">
                    <FieldTitleAndSubTitle
                        title={t('campaignEditor.requirement.conflictingCampaignListHeader')}
                        identifier={`${requirementUuid}_${fieldIdentifiers.conflictTitle}`}
                    />
                    {blockedCampaignsUuids && blockedCampaignsUuids.length > 0 && (
                        <>
                            {blockedCampaignsUuids.map((uuid, index) => (
                                <div key={index}>
                                    {blockedCampaignsData[uuid] ? (
                                        <div className="block-campaign">
                                            <div>
                                                {blockedCampaignsData[uuid].title} -{' '}
                                                <span>{blockedCampaignsData[uuid].brand}</span>
                                            </div>
                                            {!isReadOnly && (
                                                <Icon.CloseIcon
                                                    onClick={(): void => handleClickRemove(index)}
                                                    className="remove-field-icon"
                                                />
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </RequirementCardContainer>
        </BlockedCampaignsContainer>
    )
}

export default BlockedCampaigns
